// Individual patterns for different sensitive data types
const SSN_PATTERN = /(?:(?<=^)|(?<=[^0-9a-fA-F-]))\d{3}([-. ])\d{2}\1\d{4}(?:(?=$)|(?=[^0-9a-fA-F-]))/g;
const CREDIT_CARD_PATTERN =
  /(?:(?<=^)|(?<=[^0-9a-fA-F-]))(?:\d{4}-\d{4}-\d{4}-\d{4}|\d{4} \d{4} \d{4} \d{4})(?:(?=$)|(?=[^0-9a-fA-F-]))/g;
const PHONE_PATTERN =
  /(?:(?<=^)|(?<=[^0-9a-fA-F-]))(?:\(\d{3}\)\d{3}[\s.-]?\d{4}|\(\d{3}\)[\s.-]\d{3}[\s.-]\d{4}|\d{3}[\s.-]\d{3}[\s.-]\d{4}|\+1\d{10})(?:(?=$)|(?=[^0-9a-fA-F-]))/g;
const EMAIL_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const PEM_PATTERN = /-----BEGIN [A-Z ]+-----[\s\S]+?-----END [A-Z ]+-----/g;
const PEM_HEADER_PATTERN = /-----BEGIN [A-Z ]+-----/g;
const BEARER_TOKEN_PATTERN = /bearer\s+[a-zA-Z0-9._-]+/gi;
const AWS_ACCESS_KEY_PATTERN = /AKIA[0-9A-Z]{16}/g;
const SUPABASE_KEY_PATTERN = /eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g;
const SLACK_TOKEN_PATTERN = /xox[bp]-[a-zA-Z0-9-]+/g;
const PRIVATE_KEY_PATTERN = /-----BEGIN PRIVATE KEY-----[\s\S]*?-----END PRIVATE KEY-----/g;
const RSA_PRIVATE_KEY_PATTERN = /-----BEGIN RSA PRIVATE KEY-----[\s\S]*?-----END RSA PRIVATE KEY-----/g;

const MRN_PATTERN = /(?:MRN|Medical Record Number)[:\s\-]*\d{6,10}/gi;
const HEALTH_PLAN_PATTERN =
  /\b(health[_-]?plan[_-]?beneficiary[_-]?number|beneficiary[_-]?number)[:\s-]*[A-Z0-9\-]{6,15}\b/gi;
const ACCOUNT_NUMBER_PATTERN = /\baccount[_-]?number[:\s-]*[A-Z0-9\-]{6,15}\b/gi;
const VIN_PATTERN = /\b(?=.*[A-HJ-NPR-Z])(?!\d{17})[A-HJ-NPR-Z0-9]{17}\b/g;
const LICENSE_PLATE_PATTERN =
  /(?<=^|[\s,;:\.\(\)\[\]\{\}!\?])(?:[A-Z]{2,3}\d{3,4}(?:[A-Z]{0,2})?|\d{1}[A-Z]{3}\d{3}|\d{3}[A-Z]{3}|\d{3}[A-Z]{2}\d{2}|[A-Z]{2}\d{5}|[A-Z]{3}\d{4}|[A-Z]\d[A-Z]\d[A-Z]\d|\d{4}[A-Z]{2})(?=$|[\s,;:\.\(\)\[\]\{\}!\?])/;
const DEVICE_ID_PATTERN = /\bdevice[_-]?id[:\s-]*[A-Z0-9\-]{6,}\b/gi;
const IPV4_PATTERN = /\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/g;
const IPV6_PATTERN = /\b(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}\b/g;
const BIOMETRIC_PATTERN = /\b(fingerprint|voiceprint|retina|iris|face[_-]?id|biometric)[:\s-]*[A-Z0-9\-]{6,}\b/gi;
const IMAGE_PATTERN = /\b(photo|image|picture|photograph)[:\s-]*[A-Z0-9\-\.]{3,}\b/gi;

// Descriptive pattern mapping
const PATTERN_LABELS: Array<{ pattern: RegExp; label: string }> = [
  { pattern: SSN_PATTERN, label: "SSN" },
  { pattern: CREDIT_CARD_PATTERN, label: "CREDIT_CARD" },
  { pattern: PHONE_PATTERN, label: "PHONE" },
  { pattern: EMAIL_PATTERN, label: "EMAIL" },
  { pattern: JWT_PATTERN, label: "JWT" },
  { pattern: BEARER_TOKEN_PATTERN, label: "BEARER_TOKEN" },
  { pattern: AWS_ACCESS_KEY_PATTERN, label: "AWS_ACCESS_KEY" },
  { pattern: SUPABASE_KEY_PATTERN, label: "SUPABASE_KEY" },
  { pattern: SLACK_TOKEN_PATTERN, label: "SLACK_TOKEN" },
  { pattern: PRIVATE_KEY_PATTERN, label: "PRIVATE_KEY" },
  { pattern: RSA_PRIVATE_KEY_PATTERN, label: "RSA_PRIVATE_KEY" },
  { pattern: PEM_PATTERN, label: "PEM" },
  { pattern: PEM_HEADER_PATTERN, label: "PEM_HEADER" },
  { pattern: MRN_PATTERN, label: "MRN" },
  { pattern: HEALTH_PLAN_PATTERN, label: "HEALTH_PLAN" },
  { pattern: ACCOUNT_NUMBER_PATTERN, label: "ACCOUNT_NUMBER" },
  { pattern: VIN_PATTERN, label: "VIN" },
  { pattern: LICENSE_PLATE_PATTERN, label: "LICENSE_PLATE" },
  { pattern: DEVICE_ID_PATTERN, label: "DEVICE_ID" },
  { pattern: IPV4_PATTERN, label: "IPV4" },
  { pattern: IPV6_PATTERN, label: "IPV6" },
  { pattern: BIOMETRIC_PATTERN, label: "BIOMETRIC" },
  { pattern: IMAGE_PATTERN, label: "IMAGE" },
];

// Descriptive field mapping
const FIELD_LABELS: Array<{ pattern: RegExp; label: string }> = [
  // API and authentication keys
  { pattern: /api[_-]?key/i, label: "API_KEY" },
  { pattern: /^token$/i, label: "TOKEN" },
  { pattern: /^secret$/i, label: "SECRET" },
  { pattern: /client[_-]?secret/i, label: "CLIENT_SECRET" },
  { pattern: /client[_-]?id/i, label: "CLIENT_ID" },
  { pattern: /private[_-]?key/i, label: "PRIVATE_KEY" },
  { pattern: /access[_-]?key/i, label: "ACCESS_KEY" },
  { pattern: /session[_-]?key/i, label: "SESSION_KEY" },
  { pattern: /encryption[_-]?key/i, label: "ENCRYPTION_KEY" },
  { pattern: /jwt/i, label: "JWT" },
  { pattern: /bearer/i, label: "BEARER" },
  { pattern: /^authorization$/i, label: "AUTHORIZATION" },
  { pattern: /^auth$/i, label: "AUTH" },
  { pattern: /credential/i, label: "CREDENTIAL" },
  { pattern: /^certificate$/i, label: "CERTIFICATE" },
  { pattern: /^signature$/i, label: "SIGNATURE" },

  // Personal identifiers
  { pattern: /^beneficiary$/i, label: "BENEFICIARY" },
  { pattern: /^subscriber$/i, label: "SUBSCRIBER" },
  { pattern: /^full[_-]?name$/i, label: "FULL_NAME" },
  { pattern: /^first[_-]?name$/i, label: "FIRST_NAME" },
  { pattern: /^last[_-]?name$/i, label: "LAST_NAME" },
  { pattern: /^given[_-]?name$/i, label: "GIVEN_NAME" },
  { pattern: /^family[_-]?name$/i, label: "FAMILY_NAME" },
  { pattern: /^middle[_-]?name$/i, label: "MIDDLE_NAME" },
  { pattern: /^preferred[_-]?name$/i, label: "PREFERRED_NAME" },
  { pattern: /^nick[_-]?name$/i, label: "NICK_NAME" },
  { pattern: /^dob$/i, label: "DOB" },
  { pattern: /^date[_-]?of[_-]?birth$/i, label: "DATE_OF_BIRTH" },
  { pattern: /^birth[_-]?date$/i, label: "BIRTH_DATE" },
  { pattern: /^ssn$/i, label: "SSN" },
  { pattern: /^social[_-]?security[_-]?number$/i, label: "SOCIAL_SECURITY_NUMBER" },
  { pattern: /^username$/i, label: "USERNAME" },
  { pattern: /password/i, label: "PASSWORD" },

  // HIPAA Safe Harbor field names
  { pattern: /^mrn$/i, label: "MRN" },
  { pattern: /^medical[_-]?record[_-]?number$/i, label: "MEDICAL_RECORD_NUMBER" },
  { pattern: /^health[_-]?plan[_-]?beneficiary[_-]?number$/i, label: "HEALTH_PLAN_BENEFICIARY_NUMBER" },
  { pattern: /^account[_-]?number$/i, label: "ACCOUNT_NUMBER" },
  { pattern: /^vehicle[_-]?id$/i, label: "VEHICLE_ID" },
  { pattern: /^license[_-]?plate$/i, label: "LICENSE_PLATE" },
  { pattern: /^vin$/i, label: "VIN" },
  { pattern: /^device[_-]?id$/i, label: "DEVICE_ID" },
  { pattern: /^ip[_-]?address$/i, label: "IP_ADDRESS" },
  { pattern: /^biometric$/i, label: "BIOMETRIC" },
  { pattern: /^fingerprint$/i, label: "FINGERPRINT" },
  { pattern: /^voiceprint$/i, label: "VOICEPRINT" },
  { pattern: /^photo$/i, label: "PHOTO" },
  { pattern: /^image$/i, label: "IMAGE" },

  // Address-related fields
  { pattern: /^line1$/i, label: "ADDRESS_LINE1" },
  { pattern: /^line2$/i, label: "ADDRESS_LINE2" },
  { pattern: /^street$/i, label: "STREET" },
  { pattern: /^address$/i, label: "ADDRESS" },
  { pattern: /^city$/i, label: "CITY" },
  { pattern: /^district$/i, label: "DISTRICT" },
  // HIPAA Safe Harbor permits state-level geography; smaller subdivisions remain redacted.
  { pattern: /^county$/i, label: "COUNTY" },
  { pattern: /^province$/i, label: "PROVINCE" },
  { pattern: /^postal[_-]?code$/i, label: "POSTAL_CODE" },
  { pattern: /^zip$/i, label: "ZIP" },
  { pattern: /^zipcode$/i, label: "ZIPCODE" },
  { pattern: /^country$/i, label: "COUNTRY" },
];

const UNREDACTED_DELIM = "\uE000";
const UNREDACTED_REGEX = new RegExp(`${UNREDACTED_DELIM}([^${UNREDACTED_DELIM}]*)${UNREDACTED_DELIM}`, "g");

export function obfuscateEmail(email: string): string {
  return email.replace("@", " at ");
}

export function unredacted(value: string): string {
  return `${UNREDACTED_DELIM}${value}${UNREDACTED_DELIM}`;
}

function processStringValue(value: string): string {
  return value.split(UNREDACTED_REGEX).reduce((acc, part, i) => {
    if (i % 2 === 1) return acc + part;
    let redacted = part;

    for (const { pattern, label } of PATTERN_LABELS) {
      redacted = redacted.replace(pattern, `[REDACTED ${label} VALUE]`);
    }

    return acc + redacted;
  }, "");
}

function processObject(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(processObject);
  }

  if (obj && typeof obj === "object") {
    if (typeof obj.toJSON === "function") {
      return processObject(obj.toJSON());
    }

    const result: any = {};

    for (const key in obj) {
      const value = obj[key];

      // Field-level redaction - only for string values that match sensitive field names
      if (typeof value === "string") {
        const fieldLabel = FIELD_LABELS.find(({ pattern }) => pattern.test(key));

        if (fieldLabel) {
          result[key] = `[REDACTED ${fieldLabel.label} FIELD]`;
          continue;
        }

        result[key] = processStringValue(value);
      } else if (typeof value === "object" && value !== null) {
        // Recursively process nested objects and arrays
        result[key] = processObject(value);
      } else {
        // Keep non-object, non-string values as-is
        result[key] = value;
      }
    }

    return result;
  }

  if (typeof obj === "string") {
    return processStringValue(obj);
  }

  return obj;
}

export function redact<T extends object>(input: T): T {
  if (process.env.NODE_ENV === "production") {
    return processObject(input);
  }

  return input;
}
